import React, { useCallback, useContext, useState } from "react";

// API / Hooks
import { SendCode } from "../../../Api/TvAuth";
import { useConfirmDeviceCode } from "../../../Hooks/Auth/useConfirmDeviceCode";

// Models
import { TvPlatform } from "../../../Models/Enums/TvPlatforms";

// Components
import Input from "../../../Components/UI/Inputs/Input";
import ButtonPill from "../../../Components/Buttons/ButtonPill";

// Constants
import { RoutePaths } from "../../../Constants/RoutePaths";

// Assets
import { ReactComponent as IckonicLogoQuestionEverything } from "../../../Assets/Images/Logos/ickonic-question-everything-white.svg";
import IconCheckMarkHollow from "../../../Assets/SVGs/Icons/YesOrNo/CheckMarkHollow";

// Context / Helpers
import { UserAuthenticationContext } from "../../../Context/UserAuthenticationContext";
import { GetTvPlatformName } from "../../../Helpers/TVApps";

/**
 * Compatibility activation page for the long-lived /TvLogin, /Roku,
 * /AndroidTV and /SamsungTV URLs.
 *
 * Current TV apps use the v1 device flow and point customers at /Activate, but
 * old bookmarks to these branded URLs remain in circulation. These pages try
 * v1 first, then fall back to the legacy /rdv exchange only when v1 returns a
 * definitive 404. That preserves genuinely old TV clients without sending
 * current device codes exclusively to the obsolete endpoint.
 */
function TvAuthenticationScreen(props: { platform: TvPlatform }) {
    const authCtx = useContext(UserAuthenticationContext);
    const platformName = GetTvPlatformName(props.platform);

    const [code, setCode] = useState("");
    const [success, setSuccess] = useState(false);

    const confirmLegacyCode = useCallback(
        async (rawCode: string, controller: AbortController) => {
            const userId = authCtx.userData?.id;
            if (!userId) return false;
            return SendCode(rawCode, userId, controller);
        },
        [authCtx.userData?.id]
    );

    const {
        confirmDeviceCode,
        isConfirming,
        lastErrorMessage,
        clearConfirmError,
    } = useConfirmDeviceCode({
        getValidAccessToken: authCtx.getValidAccessToken,
        legacyNotFoundFallback: confirmLegacyCode,
    });

    const isAuthenticated = authCtx.doesAuthTokenExist();

    const submitCode = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!isAuthenticated || isConfirming || !code.trim()) return;

        clearConfirmError();
        setSuccess(false);

        // Validate the current value directly. The former reducer dispatched a
        // validation action and immediately read stale state, so a valid first
        // submission could fail before React applied the update.
        const result = await confirmDeviceCode(code);
        if (result.ok) setSuccess(true);
    };

    const codeForm = (
        <>
            <p style={{ marginTop: 0 }}>
                Enter the code shown on your {platformName} to link this device to your account.
            </p>

            <form onSubmit={submitCode}>
                <Input
                    type="text"
                    value={code}
                    required
                    disabled={isConfirming}
                    errorMessage={lastErrorMessage ?? undefined}
                    isValid={!lastErrorMessage}
                    placeholder={`Enter your ${platformName} authentication code here`}
                    onChange={(event) => {
                        setCode(event.target.value);
                        if (lastErrorMessage) clearConfirmError();
                        if (success) setSuccess(false);
                    }}
                    onBlur={() => undefined}
                />

                <div className="tv-apps-auth__button-wrapper">
                    <ButtonPill
                        type="submit"
                        disabled={isConfirming || !code.trim()}
                        label={isConfirming ? "Linking…" : "Link device"}
                        className={`button-wrapper__submit ${code.trim() ? "has-value" : "has-no-value"}`}
                    />
                </div>
            </form>
        </>
    );

    const unauthenticatedLayout = (
        <>
            <h3>You need an account to link your {platformName} App.</h3>

            <div className="tv-apps-auth__button-wrapper">
                <ButtonPill label="Sign Up" link={RoutePaths.SignUp[0]} />

                <p>Or</p>

                <ButtonPill
                    label="Login"
                    link={RoutePaths.Login + `?redirectTo=${props.platform}`}
                />
            </div>
        </>
    );

    const successLayout = (
        <div className="tv-apps-auth__success">
            {IconCheckMarkHollow()}
            Success! Your {platformName} will log in shortly. You can go back to your TV now.
        </div>
    );

    const displayLayout = () => {
        if (authCtx.authInitializing) {
            return <p style={{ marginTop: 0 }}>Checking your account…</p>;
        }
        if (success) return successLayout;
        if (!isAuthenticated) return unauthenticatedLayout;
        return codeForm;
    };

    return (
        <div className="page page--tv-app-auth">
            <div className="tv-apps-auth__logo">
                <IckonicLogoQuestionEverything />
            </div>

            <div className="tv-apps-auth__inner">
                <h2>{platformName} Authentication</h2>

                <div className="tv-apps-auth__divider" />

                <div className="tv-apps-auth__content">
                    {displayLayout()}
                </div>
            </div>
        </div>
    );
}

export default TvAuthenticationScreen;
